// `?scenario=NAME` boot path (IMPLEMENTATION §2.1 game/scenarios.ts, §6.1 "screenshots only with
// ?test=1&seed=42&scenario=…"): shared/scenarios/NAME.json through the sim's scenario loader. The files
// are lazy chunks (never in the initial bundle, fetched only with ?scenario in a hooks-enabled build). The
// scenario's timed commands are applied by the controller before the tick they are due in, exactly like
// `runScenario`, until the scenario's `ticks` have run; then the board just keeps running.
import type { Command } from '../sim/commands.ts';
import type { Int } from '../sim/core/types.ts';
import type { SimDefs } from '../sim/defs.ts';
import {
  checkScenario,
  isDue,
  resolveCommand,
  type ScenarioDef,
  scenarioState,
} from '../sim/scenario.ts';
import type { SimState } from '../sim/state.ts';

const files = import.meta.glob('../../../shared/scenarios/*.json', { import: 'default' });

/** Scenario names bundled with the app. */
export function scenarioNames(): string[] {
  return Object.keys(files)
    .map((p) => p.slice(p.lastIndexOf('/') + 1, -'.json'.length))
    .sort();
}

/** Loads and checks scenario `name` (throws with the reason when it is unknown or invalid). */
export async function loadScenario(name: string): Promise<ScenarioDef> {
  const key = Object.keys(files).find((p) => p.endsWith(`/${name}.json`));
  const load = key !== undefined ? files[key] : undefined;
  if (!load) throw new Error(`unknown scenario "${name}" (have: ${scenarioNames().join(', ')})`);
  return checkScenario(await load());
}

export interface ScenarioStart {
  state: SimState;
  runner: ScenarioRunner;
}

/** The scenario's start state (`seed` overrides the file's seed) and its command runner. */
export function startScenario(d: SimDefs, def: ScenarioDef, seed: Int | null): ScenarioStart {
  const withSeed: ScenarioDef = seed === null ? def : { ...def, seed };
  return { state: scenarioState(d, withSeed), runner: new ScenarioRunner(withSeed) };
}

export class ScenarioRunner {
  readonly def: ScenarioDef;

  constructor(def: ScenarioDef) {
    this.def = def;
  }

  /**
   * Dispatches the commands due before tick `t` + 1, in file order, each resolved against the live state
   * right before it is applied (like `runScenario`).
   */
  applyDue(t: Int, state: () => SimState, d: SimDefs, dispatch: (cmd: Command) => void): void {
    if (t >= this.def.ticks) return;
    for (const c of this.def.commands ?? [])
      if (isDue(c, t)) dispatch(resolveCommand(state(), d, c.cmd));
  }
}
